import { cn, Pressable } from '@vidahealth/vida-ui';
import { cssInterop } from 'nativewind';
import { useEffect, useRef } from 'react';
import { Animated, Easing, Platform, View } from 'react-native';

import { FrostedSurface } from '#modules/ui/FrostedSurface';
import { ChevronLeftIcon } from '#modules/ui/icons';

// RN's `Animated.View` isn't auto-wired by nativewind, so a bare `className`
// prop is silently dropped. Without this, the progress fill renders with no
// background color (invisible bar). Same pattern as Autocomplete's wrapping
// of Reanimated's Animated.View.
cssInterop(Animated.View, { className: 'style' });

const ANIMATION_DURATION_MS = 350;

/**
 * The vertical space `OnboardingHeader` occupies when `visible`, in pixels —
 * for the `(onboarding)` layout to reserve via `ScreenTopInsetProvider` up
 * front, without waiting on a measured `onLayout` (which would flash body
 * content up by the header's height on the very first frame, since an
 * absolutely positioned `FrostedSurface` reports its own size but never
 * contributes to a parent's). `OnboardingHeader` itself is not in this
 * package's `exports` map or `files` — it's an onboarding-app-local
 * component, not something an external host can import.
 *
 * Mirrors the component's own render below EXACTLY: the caller's safe-area
 * `paddingTop`, web's extra `mt-8` breathing room, and the `h-12` row. The
 * `(onboarding)` layout (which measures once for the whole app) relies on
 * this staying in sync with the JSX rather than drifting into its own copy —
 * so change both together if either changes.
 */
export function getOnboardingHeaderHeight({
  insetTop,
  visible,
}: {
  insetTop: number;
  visible: boolean;
}): number {
  if (!visible) return 0;
  return insetTop + (Platform.OS === 'web' ? 32 : 0) + 48;
}

type OnboardingHeaderProps = {
  /** Max content width to align with the body column on wide viewports. */
  maxContentWidth: number;
  /**
   * Top padding for the safe-area inset (e.g. the device status bar on
   * native). Applied above the row so the back button and progress bar sit
   * below system chrome. Defaults to 0.
   */
  paddingTop?: number;
  /**
   * Progress fraction (0–1) for the bar, or `null` to hide it and render a
   * spacer instead. Supplied by the caller rather than read from the rules
   * engine — see the note on the component below.
   */
  progress: number | null;
  /** Whether the back chevron may be shown. */
  canShowBack?: boolean;
  /** Back action. The chevron renders only when `canShowBack` and this are both set. */
  onBack?: (() => void) | null;
  /**
   * Whether the chrome renders at all. Steps that opt out (e.g.
   * /account-creation) pass `false`, as does the in-flight RTE eligibility
   * check. Defaults to `true`.
   *
   * A PROP rather than the caller conditionally mounting this component — see
   * the note on the component below. Rendering `null` from here keeps the ref
   * holding the progress `Animated.Value` (and so its current position) alive;
   * unmounting discards it and the bar refills from empty on return.
   */
  visible?: boolean;
};

/**
 * Persistent header for the onboarding flow: back chevron + animated progress
 * bar, wrapped in an absolute-positioned FrostedSurface that overlays the
 * screen content so the content can scroll behind it.
 *
 * Purely presentational: step state (progress, back availability + action)
 * arrives as PROPS. It deliberately does NOT read `useOnboardingSteps`.
 * `@/modules/ui` is a barrel and Metro doesn't tree-shake, so that one import
 * put `ui → rulesEngine → analytics` in the graph of *every* ui consumer —
 * including `@/modules/dynamicAssessments`, whose whole point is to mount without the rules
 * engine, Datadog or PostHog. `app/(onboarding)/_layout.tsx` owns the wiring;
 * the rules engine still owns "what does back mean here?" via the `onBack` it
 * passes in. Guarded by `src/packaging.test.ts`.
 *
 * Visibility is a prop, and the caller must KEEP THIS MOUNTED and toggle
 * `visible` rather than conditionally rendering it. The progress fill is an
 * `Animated.Value` held in a ref here, so unmounting resets it to 0 and the next
 * visible step animates the bar up from empty instead of resuming where it left
 * off. That isn't only a first-step concern: `canShowHeader` also goes false
 * MID-FLOW for the duration of an in-flight RTE eligibility check
 * (/employee-search, /employee-zip-verification), so a member would watch the
 * bar refill after every check.
 *
 * What survives a hidden interval is precisely the ref — NOT the rendered node.
 * `return null` still unmounts the `Animated.View`, and RN detaches the value
 * once its last child goes (`__detach` → `stopAnimation` + `dropAnimatedNode`),
 * so an in-flight fill is cancelled. The `Animated.Value` object and its current
 * number live on in the ref, which is all this needs: on return the effect
 * animates from that retained position to the same target — visually a no-op —
 * where a remounted header would animate 0 → target. Guarded by the hide/show
 * test, which asserts the animation's starting value rather than a call count,
 * since both versions animate the same number of times.
 *
 * Note the caller passes `progress: null` whenever it passes `visible={false}`
 * (`useOnboardingSteps` returns `showHeader ? calculateProgress(path) : null`),
 * so the effect below takes its null branch while hidden and tracks nothing.
 *
 * The layout's own `routeReady` latch exists for the same reason — see its
 * comment about the header staying mounted so the bar doesn't flicker on every
 * Continue.
 *
 * `getOnboardingHeaderHeight` returns 0 for a header-hidden step, so the
 * layout's `ScreenTopInsetProvider` reservation drops to 0 too — a
 * header-hidden step's body sits higher than a header-visible one's, not at
 * the same y-position. Only visibility toggling WHILE mounted (the in-flight
 * RTE check) is what this component's `return null` avoids disturbing — see
 * above.
 */
export function OnboardingHeader({
  maxContentWidth,
  paddingTop = 0,
  progress: target,
  canShowBack = false,
  onBack,
  visible = true,
}: OnboardingHeaderProps) {
  const progressVisible = target !== null;

  // Progress fill is driven via scaleX on an Animated.Value so the timing
  // runs on the native driver (off the JS thread, smooth at 60fps even
  // when JS is busy). Initialized to 0 so the first visible step animates
  // in from empty — gives the user a visible "advancing" cue.
  const progressRef = useRef<Animated.Value | null>(null);
  if (progressRef.current === null) progressRef.current = new Animated.Value(0);
  const progress = progressRef.current;

  // Track the previous target so we only animate on actual target changes.
  // Same-target re-renders (navigating between sibling steps in the same
  // group, e.g. /company-search ↔ /employer-or-health-plan-verification)
  // leave the bar untouched.
  const previousTargetRef = useRef<number | null>(null);

  // Runs whether or not we're visible. While hidden the caller passes a null
  // target, so this takes the null branch and clears the tracking ref, leaving
  // the retained value untouched for the next visible render to resume from.
  useEffect(() => {
    if (target === null) {
      previousTargetRef.current = null;
      return;
    }
    if (previousTargetRef.current !== target) {
      Animated.timing(progress, {
        toValue: target,
        duration: ANIMATION_DURATION_MS,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }).start();
    }
    previousTargetRef.current = target;
  }, [target, progress]);

  // Render nothing, but stay mounted — the refs above are the point.
  if (!visible) return null;

  return (
    <FrostedSurface className='absolute left-0 right-0 top-0'>
      <View
        // Reserve safe-area space (status bar on native, zero on web) above
        // the row. On web, also add visual breathing room with mt-8 since
        // there's no system bar to push us down. On native, the safe-area
        // inset alone is enough — stacking another margin would double up
        // against the inset.
        style={{ paddingTop }}
      >
        <View
          className={cn(
            'mx-auto h-12 w-full flex-row items-center gap-4 px-6',
            Platform.OS === 'web' ? 'mt-8' : ''
          )}
          style={{ maxWidth: maxContentWidth }}
        >
          {canShowBack && onBack ? (
            <Pressable
              onPress={onBack}
              testID='onboarding-back'
              accessibilityRole='button'
              accessibilityLabel='Go back'
              className='h-10 w-10 items-center justify-center rounded-full bg-background-50'
            >
              <ChevronLeftIcon size={24} className='text-typography-900' />
            </Pressable>
          ) : null}
          {progressVisible ? (
            // Outer track clips the scaled fill back to a rounded pill so the
            // right edge keeps its border-radius. The inner Animated.View is a
            // full-width bar transformed via scaleX with a left-anchored
            // origin — transform animations don't need a layout pass per frame.
            <View className='h-1.5 flex-1 overflow-hidden rounded-full bg-background-50 dark:bg-outline-100'>
              <Animated.View
                testID='onboarding-progress-fill'
                style={{
                  height: '100%',
                  width: '100%',
                  transformOrigin: 'left',
                  transform: [{ scaleX: progress }],
                }}
                className='bg-primary-500'
              />
            </View>
          ) : (
            // Empty spacer keeps the back button hugging the left edge so the
            // layout stays stable across screens that show vs. hide the bar.
            <View className='flex-1' />
          )}
        </View>
      </View>
    </FrostedSurface>
  );
}
